import { useQuery } from '@tanstack/react-query';

// common
import { KEYS_QUERY } from 'common/constants';
import { handleException } from 'modules/account/detail/helpers/utils';
import { OrganizationTypeId } from 'src/enum/combobox';
import useGetHvsInfo from 'modules/account/services/useGetHvsInfo';
import useGetListClients from 'modules/account/services/useGetListClients';
import useGetListGovAgencies from 'modules/account/services/useGetListGovAgencies';

export interface OrganizationInfo {
  operationCode: string;
  name: string;
  orgType: string;
  status: boolean;
}

export const convertOrgName = (orgType: string) => {
  if (!orgType) return '';
  if (orgType === 'HVS') return 'Công ty TNHH Hương Việt Sinh';
  if (orgType === 'KHDN') return 'Khách hàng doanh nghiệp';
  if (orgType === 'CQQL') return 'Cơ quan quản lý';
  return orgType;
};

export const formatUnits = (arr: any[], orgType: OrganizationTypeId) => {
  return arr.map(({ operationCode, name }) => {
    return { operationCode, name, orgType, status: true } as OrganizationInfo;
  });
};

/**
 * Dữ liệu hiển thị cho màn Danh sách đơn vị - phòng ban
 */
const useGetOrganizationsList = () => {
  const { data: hvsInfo, isLoading: isLoadingHvs, isSuccess: isHvsSuccess } = useGetHvsInfo();

  const {
    data: clients,
    isLoading: isLoadingClients,
    isSuccess: isClientsSuccess,
  } = useGetListClients();

  const {
    data: govAgencies,
    isLoading: isLoadingGovAgencies,
    isSuccess: isGovAgenciesSuccess,
  } = useGetListGovAgencies();

  const hvsUnit: OrganizationInfo[] = hvsInfo && hvsInfo.unit ? [hvsInfo.unit] : [];

  const allQueriesCompleted = isHvsSuccess && isClientsSuccess && isGovAgenciesSuccess;
  const anyQueryLoading = isLoadingHvs || isLoadingClients || isLoadingGovAgencies;

  return useQuery({
    queryKey: [KEYS_QUERY.kiosClients, KEYS_QUERY.kiosGovAgencies, KEYS_QUERY.kiosHvs],
    queryFn: async () => {
      try {
        return [
          ...formatUnits(hvsUnit, 'HVS'),
          ...formatUnits(clients ?? [], 'KHDN'),
          ...formatUnits(govAgencies ?? [], 'CQQL'),
        ];
      } catch (error) {
        handleException(error);
        return [];
      }
    },
    // Only run this query when all dependent queries have completed successfully
    enabled: allQueriesCompleted && !anyQueryLoading,
    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,
  });
};

export default useGetOrganizationsList;
